import React from 'react';

const VolatilityTrading = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Understanding Volatility",
      icon: "⚡",
      content: {
        keyPoints: [
          "Volatility measures how much an asset's price moves",
          "Historical volatility looks at past price movements",
          "Implied volatility reflects market expectations",
          "Higher volatility = higher option premiums"
        ],
        example: "BTC moving 2% daily = low volatility. BTC moving 10% daily = high volatility. Options cost more during high volatility periods."
      },
      quiz: [
        {
          id: 'q1',
          question: "What does volatility measure?",
          options: ["Price direction", "Price movement magnitude", "Option strike prices", "Time to expiration"],
          correct: 1,
          explanation: "Correct! Volatility measures the magnitude of price movements, not direction. High volatility means bigger price swings."
        },
        {
          id: 'q2',
          question: "When volatility is high, option premiums are:",
          options: ["Lower", "Higher", "Unchanged", "Unpredictable"],
          correct: 1,
          explanation: "Correct! Higher volatility increases option premiums because there's more chance for large profitable moves."
        }
      ]
    },
    2: {
      title: "Long Straddles",
      icon: "🎯",
      content: {
        keyPoints: [
          "Buy call + put at same strike and expiration",
          "Profit from large moves in either direction",
          "Maximum loss = total premium paid",
          "Breakeven = strike ± premium paid"
        ],
        example: "BTC at $50k. Buy $50k call for $2k + $50k put for $2k. Total cost $4k. Profit if BTC moves below $46k or above $54k."
      },
      quiz: [
        {
          id: 'q1',
          question: "A long straddle profits when:",
          options: ["Price stays flat", "Price moves significantly up or down", "Only when price goes up", "Only when price goes down"],
          correct: 1,
          explanation: "Correct! Long straddles profit from large moves in EITHER direction because you own both calls and puts."
        },
        {
          id: 'q2',
          question: "The maximum loss in a long straddle is:",
          options: ["Unlimited", "The premium paid", "The strike price", "Zero"],
          correct: 1,
          explanation: "Correct! Maximum loss is limited to the premium paid."
        }
      ]
    },
    3: {
      title: "Short Strangles",
      icon: "🎪",
      content: {
        keyPoints: [
          "Sell call and put at different strikes",
          "Profit from sideways movement and time decay",
          "Maximum profit = total premium collected",
          "Risk if price moves outside the strikes"
        ],
        example: "BTC at $50k. Sell $45k put for $1k + sell $55k call for $1k. Collect $2k. Profit if BTC stays between $45k-$55k."
      },
      quiz: [
        {
          id: 'q1',
          question: "Short strangles profit when:",
          options: ["Price moves significantly", "Price stays between strikes", "Volatility increases", "Time passes quickly"],
          correct: 1,
          explanation: "Correct! Short strangles profit when price stays between the short strikes, allowing time decay to work in your favor."
        },
        {
          id: 'q2',
          question: "What's the maximum profit in a short strangle?",
          options: ["Unlimited", "Premium collected", "Strike difference", "Underlying price"],
          correct: 1,
          explanation: "Correct! Maximum profit equals the total premium collected from selling both options."
        }
      ]
    },
    4: {
      title: "Volatility Skew",
      icon: "📈",
      content: {
        keyPoints: [
          "Different implied volatilities across strikes",
          "Put skew: higher volatility for lower strikes",
          "Reflects market fear and demand imbalances",
          "Affects option pricing and strategy selection"
        ],
        example: "BTC $45k puts may have 80% implied volatility while $55k calls have 60%, showing fear bias."
      },
      quiz: [
        {
          id: 'q1',
          question: "What does volatility skew show?",
          options: ["Time decay rates", "Different implied volatilities across strikes", "Interest rate changes", "Strike price levels"],
          correct: 1,
          explanation: "Correct! Volatility skew shows how implied volatility varies across different strike prices."
        },
        {
          id: 'q2',
          question: "Put skew typically means:",
          options: ["Puts have lower volatility", "Puts have higher volatility", "All options equal volatility", "Calls are more expensive"],
          correct: 1,
          explanation: "Correct! Put skew means put options have higher implied volatility, often due to fear of downside moves."
        }
      ]
    },
    5: {
      title: "Volatility Term Structure",
      icon: "📊",
      content: {
        keyPoints: [
          "Implied volatility across different expiration dates",
          "Front-month vs back-month volatility patterns",
          "Contango: longer-term higher volatility",
          "Backwardation: near-term higher volatility"
        ],
        example: "BTC 30-day options at 60% volatility, 90-day at 65% (contango). Events can cause backwardation."
      },
      quiz: [
        {
          id: 'q1',
          question: "What is volatility term structure?",
          options: ["Strike price relationships", "Volatility across expirations", "Option pricing models", "Time decay patterns"],
          correct: 1,
          explanation: "Correct! Term structure shows how implied volatility changes across different expiration dates."
        },
        {
          id: 'q2',
          question: "Contango in volatility means:",
          options: ["Short-term higher than long-term", "Long-term higher than short-term", "All terms equal", "Declining volatility"],
          correct: 1,
          explanation: "Correct! Contango means longer-term options have higher implied volatility than shorter-term options."
        }
      ]
    },
    6: {
      title: "Delta-Neutral Trading",
      icon: "⚖️",
      content: {
        keyPoints: [
          "Hedge directional risk to isolate volatility exposure",
          "Dynamically adjust delta as underlying moves",
          "Profit from volatility changes, not price direction",
          "Requires active management and monitoring"
        ],
        example: "Own 100 BTC calls (delta 50 each). Sell 50 BTC to achieve delta-neutral position."
      },
      quiz: [
        {
          id: 'q1',
          question: "What's the goal of delta-neutral trading?",
          options: ["Maximize directional exposure", "Eliminate directional risk", "Increase time decay", "Reduce option costs"],
          correct: 1,
          explanation: "Correct! Delta-neutral trading eliminates directional risk to focus on volatility and time decay profits."
        },
        {
          id: 'q2',
          question: "Delta-neutral positions require:",
          options: ["Set and forget approach", "Active management", "High leverage", "Long-only positions"],
          correct: 1,
          explanation: "Correct! Delta-neutral positions need active management as delta changes with price movements."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced volatility trading concepts</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-purple-500 pl-4">
            <h4 className="text-white font-bold mb-2">⚡ Key Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-indigo-500/20 border border-indigo-500/50 rounded-lg p-4">
            <h5 className="text-indigo-400 font-bold mb-2">📊 Practical Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Volatility Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these concepts to trade volatility</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-purple-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `volatility_trading_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Volatility Trader'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'volatility_trading'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Volatility Trading Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default VolatilityTrading;
